// Self-contained Credential (+ CredentialType) client — targets the same real backend routes
// flow-studio-api's credentialApiClient.ts already calls successfully
// (BizFirst.Ai.AIExtension.Api.Base's BaseCredentialController / BaseCredentialTypeController).
//
// Delete is wired into the UI for the first time here (the confirmed gap: credentialApiClient.ts
// already has a working deleteById, but zero UI in flow-studio ever calls it) — this client's
// deleteById is a direct, unmodified port of that already-real method.
//
// Deliberately does NOT depend on Atlas Forms (@atlas-forms/player-components-react) the way
// flow-studio's CredentialDialog.tsx does for its dynamic per-type form — that ties credential
// editing to a theme store (`useUIStore` from `@flow-studio/store`, workspace-local) on top of
// Atlas Forms itself. Instead this client calls the same `form-schema` endpoint but consumes the
// simpler, already-defined-but-previously-unused `CredentialFormSchema` shape
// (credential.types.ts's `controls: [...]`) and CredentialEditor.tsx renders that schema with
// plain HTML controls. OAuth2 credential types are flagged (`CredentialTypeDTO.IsOAuth`) but the
// popup-based authorize-code-exchange flow itself is out of scope for this pass — see
// DevelopmentHistoryLog.md's "Deferred" section. A user can still create/edit/delete an OAuth-type
// credential's non-token fields; the token fields must be pasted in manually for now.

import { BaseAdminApiClient, type AdminApiConfig, type AdminApiResult } from './baseAdminApiClient';
import type {
  CredentialDTO,
  CredentialTypeDTO,
  CredentialFormSchemaResponse,
  DecryptedCredentialData,
} from '../types';

export interface CreateCredentialRequest {
  Name: string;
  Description?: string;
  CredentialTypeID: number;
  EncryptedData: string;
  ExecutionScopeID: number;
  IsShared: boolean;
}

export class CredentialAdminApiClient extends BaseAdminApiClient {
  constructor(config: AdminApiConfig) {
    super(config);
  }

  async getActive(): Promise<AdminApiResult<CredentialDTO[]>> {
    const result = await this.postQuery<CredentialDTO[]>('/api/v1/ai-extension/credentials/active', {
      PageInfo: { PageNumber: 1, PageSize: 1000 },
    });
    return { ...result, data: Array.isArray(result.data) ? result.data : [] };
  }

  async getByTypeCode(typeCode: string): Promise<AdminApiResult<CredentialDTO[]>> {
    const result = await this.postQuery<CredentialDTO[]>('/api/v1/ai-extension/credentials/by-type-code', {
      TypeCode: typeCode,
      PageInfo: { PageNumber: 1, PageSize: 1000 },
    });
    return { ...result, data: Array.isArray(result.data) ? result.data : [] };
  }

  async getById(credentialId: number): Promise<AdminApiResult<CredentialDTO>> {
    return this.postQuery<CredentialDTO>('/api/v1/ai-extension/credentials/get-by-id', { ID: { ID: credentialId } });
  }

  /**
   * The backend returns the vault's decrypted payload as a bare JSON string (`GoWebResponse<string>`),
   * not an object — see DecryptedCredentialData's doc comment for the 2026-09-14 incident this
   * caused when a caller skipped this parse step. Parse it here so every consumer gets the real
   * field bag instead of silently getting `{}` back.
   */
  async getByIdDecrypted(credentialId: number): Promise<AdminApiResult<DecryptedCredentialData>> {
    const result = await this.postQuery<string>('/api/v1/ai-extension/credentials/get-by-id/decrypted', {
      CredentialID: { ID: credentialId },
    });
    if (!result.success || result.data === undefined) {
      return result as unknown as AdminApiResult<DecryptedCredentialData>;
    }
    if (typeof result.data !== 'string') {
      // Defensive — already an object (e.g. a future backend change). Pass through unchanged.
      return result as unknown as AdminApiResult<DecryptedCredentialData>;
    }
    try {
      const parsed: unknown = JSON.parse(result.data);
      return {
        ...result,
        data: parsed && typeof parsed === 'object' ? (parsed as DecryptedCredentialData) : {},
      };
    } catch {
      // Do NOT fall back to {} here — an empty field bag looks identical to "no secrets set" to
      // the caller, and CredentialEditor.tsx would happily save that over the real encrypted data.
      // Surface a failure instead so the editor can block saving.
      return {
        success: false,
        error: { code: 'DECRYPT_PARSE_ERROR', message: 'Failed to parse decrypted credential data.' },
      };
    }
  }

  async getCredentialTypes(): Promise<AdminApiResult<CredentialTypeDTO[]>> {
    const result = await this.postQuery<CredentialTypeDTO[]>('/api/v1/ai-extension/credential-types/list', {
      PageInfo: { PageNumber: 1, PageSize: 100 },
    });
    return { ...result, data: Array.isArray(result.data) ? result.data : [] };
  }

  async getFormSchema(credentialTypeId: number): Promise<AdminApiResult<CredentialFormSchemaResponse>> {
    return this.postQuery<CredentialFormSchemaResponse>('/api/v1/ai-extension/credentials/form-schema', {
      CredentialTypeID: credentialTypeId,
    });
  }

  async create(request: CreateCredentialRequest): Promise<AdminApiResult<CredentialDTO>> {
    const response = await this.post<{ id?: { id?: number } }>('/api/v1/ai-extension/credentials', request);
    const newID = response.data?.id?.id;
    if (response.success && newID) {
      return {
        ...response,
        data: {
          CredentialID: newID,
          Name: request.Name,
          Description: request.Description,
          CredentialTypeID: request.CredentialTypeID,
          ExecutionScopeID: request.ExecutionScopeID,
          IsShared: request.IsShared,
        } as CredentialDTO,
      };
    }
    return response as unknown as AdminApiResult<CredentialDTO>;
  }

  async update(credentialId: number, request: Omit<CreateCredentialRequest, 'ExecutionScopeID'> & { ExecutionScopeID?: number }): Promise<AdminApiResult<CredentialDTO>> {
    const response = await this.put<{ id?: { id?: number } }>(`/api/v1/ai-extension/credentials/${credentialId}`, {
      CredentialID: credentialId,
      ...request,
    });
    if (response.success) {
      return {
        ...response,
        data: {
          CredentialID: credentialId,
          Name: request.Name,
          Description: request.Description,
          CredentialTypeID: request.CredentialTypeID,
          ExecutionScopeID: request.ExecutionScopeID,
          IsShared: request.IsShared,
        } as CredentialDTO,
      };
    }
    return response as unknown as AdminApiResult<CredentialDTO>;
  }

  /** Real gap fix — the backend/client-method side of this already existed; only the UI never called it. */
  async deleteById(credentialId: number): Promise<AdminApiResult<void>> {
    return this.del<void>(`/api/v1/ai-extension/credentials/${credentialId}`, { id: credentialId });
  }
}
